import { overrideTheme } from '@aspendental/themes';
import { createTheme, GlobalStyles, GlobalStylesProps, ThemeProvider, ScopedCssBaseline } from '@mui/material';
import { match } from 'css-mediaquery';
import dynamic from 'next/dynamic';

import LazyHydrate from '@shared-components-web/components/LazyHydrate/LazyHydrate';
import { useAppContext } from '@shared-components-web/context/AppContextProvider.ctx';
import { PageTheme } from '@shared-components-web/context/PageThemeProvider.ctx';
import * as features from '@shared-components-web/theme/tagfeatures';
import { TAGBrand } from '@shared-components-web/types';
import { applyFluidTypography } from '@shared-components-web/utils/applyFluidTypography/applyFluidTypography';
import { resolveBrandThemeConfig } from '@shared-components-web/utils/resolveBrandThemeConfig';

const FontPreloader = dynamic(() =>
	import(/* webpackChunkName:"FontPreloader" */ './FontPreloader').then((mod) => mod.FontPreloader)
);

declare module '@mui/material/styles' {
	interface Theme {
		brand: string;
		tagFeatures: {
			PAGE_THEME_ENABLED: boolean;
		};
	}
	interface ThemeOptions {
		brand: string;
		tagFeatures?: {
			PAGE_THEME_ENABLED?: boolean;
		};
	}
}

const globalStyles: GlobalStylesProps['styles'] = {
	html: { scrollBehavior: 'smooth' },
	'html, body': {
		width: '100%',
		margin: 0,
	},
	body: {
		overflowX: 'hidden',
	},
	'@media screen and (prefers-reduced-motion: reduce)': {
		html: {
			scrollBehavior: 'auto',
		},
	},
};
export interface TAGThemeProviderProps {
	branding: TAGBrand & string;
	pageTheme?: PageTheme;
	children?: React.ReactNode;
	deviceType?: 'desktop' | 'mobile';
}

export default function TAGThemeProvider({
	branding = TAGBrand.AspenDental,
	pageTheme,
	children,
	deviceType = 'mobile',
}: Readonly<TAGThemeProviderProps>) {
	const isMottoTheme = pageTheme === 'Motto';
	const brandFeatures = features[branding];
	const brandTheme = resolveBrandThemeConfig(isMottoTheme ? TAGBrand.Motto : branding);
	const { config } = useAppContext();
	const enableFluidTypography = !!config.featureFlags?.enableFluidTypography;

	const updatedMuiTypography = enableFluidTypography
		? applyFluidTypography(brandTheme, branding ?? null).components?.MuiTypography
		: brandTheme.components?.MuiTypography;

	const theme = createTheme(
		overrideTheme(brandTheme, {
			brand: branding,
			tagFeatures: {
				...brandFeatures,
			},
			components: {
				// https://mui.com/material-ui/react-use-media-query/#server-side-rendering
				MuiUseMediaQuery: {
					defaultProps: {
						ssrMatchMedia: (query) => ({
							matches: match(query, {
								width: deviceType === 'mobile' ? '0px' : '901px',
							}),
						}),
					},
				},
				MuiTypography: {
					...brandTheme.components?.MuiTypography,
					...updatedMuiTypography,
				},
			},

			breakpoints: {
				...brandTheme.breakpoints,
			},
		})
	);

	return (
		<ThemeProvider theme={theme}>
			<LazyHydrate ssrOnly>
				<FontPreloader brand={branding} />
			</LazyHydrate>
			<ScopedCssBaseline id="mui-scoped-baseline">
				<GlobalStyles styles={globalStyles} />
				{children}
			</ScopedCssBaseline>
		</ThemeProvider>
	);
}
